import { Component, Inject, OnInit, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PlayerAuctionService } from '../player-auction.service';

export interface SellContractLogDialogData {
  contractId: string;
}

interface LogLine {
  raw: string;
  timestamp: string;
  source: string;
  level: string;
  message: string;
  cssClass: string;
}

@Component({
  selector: 'app-sell-contract-log-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './sell-contract-log-dialog.html',
  styleUrl: './sell-contract-log-dialog.scss',
})
export class SellContractLogDialog implements OnInit, AfterViewChecked {
  @ViewChild('logContainer') logContainer!: ElementRef<HTMLElement>;

  lines: LogLine[] = [];
  loading = true;
  autoScroll = true;
  private shouldScroll = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SellContractLogDialogData,
    private dialogRef: MatDialogRef<SellContractLogDialog>,
    private service: PlayerAuctionService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadLogs();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.autoScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  loadLogs(): void {
    this.loading = true;
    this.service.getContractLog(this.data.contractId).subscribe({
      next: raw => {
        this.lines = this.parseLog(raw);
        this.loading = false;
        this.shouldScroll = true;
      },
      error: () => {
        this.snackBar.open('Error loading logs', 'Close', { duration: 5000 });
        this.loading = false;
      },
    });
  }

  private parseLog(raw: string): LogLine[] {
    return raw
      .split('\n')
      .filter(line => line.trim().length > 0)
      .map(line => {
        const match = line.match(/^\[([^\]]+)\]\s*\[([^\]]+)\]\s*\[([^\]]+)\]\s*(.*)/);
        if (!match) {
          return { raw: line, timestamp: '', source: '', level: '', message: line, cssClass: 'log-default' };
        }
        const [, timestamp, source, level, message] = match;
        return {
          raw: line,
          timestamp,
          source,
          level,
          message,
          cssClass: this.getCssClass(source, level),
        };
      });
  }

  private getCssClass(source: string, level: string): string {
    if (level === 'ERROR') return 'log-error';
    if (level === 'WARN') return 'log-warn';
    if (source === 'customer') return 'log-customer';
    if (source === 'system') return 'log-system';
    return 'log-default';
  }

  private scrollToBottom(): void {
    if (this.logContainer) {
      const el = this.logContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  onScroll(): void {
    if (!this.logContainer) return;
    const el = this.logContainer.nativeElement;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    this.autoScroll = atBottom;
  }

  toggleAutoScroll(): void {
    this.autoScroll = !this.autoScroll;
    if (this.autoScroll) this.scrollToBottom();
  }

  close(): void {
    this.dialogRef.close();
  }
}
